import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { CRM_B2C_FORGOT_PASSWORD_RECOVERY_SESSION_KEY } from 'src/app/app-config';
import { isBenignMsalRedirectError } from 'src/app/auth/msal.setup';
import { MsalRedirectService } from 'src/app/auth/msal-redirect.service';
import { Subject, from, of, Observable } from 'rxjs';
import { concatMap, takeUntil } from 'rxjs/operators';

/** Forgot-password: URL and/or `CRM_B2C_FORGOT_PASSWORD_RECOVERY_SESSION_KEY` (set before redirect) → reset policy. */
@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  public readonly buildNumber = `v${environment.buildNumber}`;
  /** Spinner on first paint: URL may not have 90118 yet; session flag set before redirect from App/CrmErrorHandler. */
  isLoading =
    LoginComponent.urlIndicatesB2CPasswordResetRequired() ||
    LoginComponent.forgotPasswordRecoverySessionPending();

  /** Same shape as MSAL / B2C forgot-password signals — match AppComponent.b2cForgotPasswordError. */
  private static msalCatchIndicatesB2cForgotPassword(err: unknown): boolean {
    if (!err || typeof err !== 'object') {
      return false;
    }
    const e = err as { errorCode?: string; errorMessage?: string; message?: string };
    const blob = `${e.errorCode ?? ''} ${e.errorMessage ?? ''} ${e.message ?? ''}`;
    return blob.includes('AADB2C90118');
  }

  private static forgotPasswordRecoverySessionPending(): boolean {
    try {
      return sessionStorage.getItem(CRM_B2C_FORGOT_PASSWORD_RECOVERY_SESSION_KEY) === '1';
    } catch {
      return false;
    }
  }

  /** Clears the one-shot session flag; call only when starting the reset redirect path. */
  private static consumeForgotPasswordRecoverySessionFlag(): boolean {
    try {
      const k = CRM_B2C_FORGOT_PASSWORD_RECOVERY_SESSION_KEY;
      if (sessionStorage.getItem(k) === '1') {
        sessionStorage.removeItem(k);
        return true;
      }
    } catch {
      /* ignore */
    }
    return false;
  }

  private static urlIndicatesB2CPasswordResetRequired(): boolean {
    const urlSearch = window.location.search;
    const urlHash = window.location.hash;
    return (
      urlSearch.includes('error=AADB2C90118') ||
      urlHash.includes('error=AADB2C90118') ||
      urlSearch.includes('AADB2C90118') ||
      urlHash.includes('AADB2C90118')
    );
  }

  /**
   * Drop the B2C forgot-password error from the address bar so a later return to `/` does not
   * re-trigger reset. Call only after MSAL redirect handling when needed.
   */
  private static removeB2cForgotPasswordErrorFromUrl(): void {
    const u = window.location;
    if (!`${u.search}${u.hash}`.includes('AADB2C90118')) {
      return;
    }
    const pathOnly = u.pathname || '/';
    if (u.search.includes('AADB2C90118')) {
      window.history.replaceState(window.history.state, document.title, `${pathOnly}${u.hash}`);
    } else if (u.hash.includes('AADB2C90118')) {
      window.history.replaceState(window.history.state, document.title, pathOnly);
    }
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) {
    if (this.authService.instance.getAllAccounts().length <= 0) {
      this.router.navigate(['']);
    }
  }

  constructor(
    private router: Router,
    private authService: MsalService,
    private msalRedirectService: MsalRedirectService,
  ) {}

  ngOnInit(): void {
    const fromSession = LoginComponent.consumeForgotPasswordRecoverySessionFlag();
    const fromUrl = LoginComponent.urlIndicatesB2CPasswordResetRequired();
    const needsPasswordReset = fromUrl || fromSession;

    if (needsPasswordReset) {
      this.isLoading = true;
      this.whenMsalIdle(() => {
        LoginComponent.removeB2cForgotPasswordErrorFromUrl();
        return from(
          this.authService.instance.loginRedirect({
            scopes: ['openid', 'profile'],
            authority: environment.passwordResetAuthority,
          }),
        );
      });
      return;
    }

    if (this.isLoading) {
      return;
    }

    const urlSearch = window.location.search;
    const urlHash = window.location.hash;

    const hasRedirectParams =
      urlSearch.includes('state=') ||
      urlSearch.includes('code=') ||
      urlHash.includes('state=') ||
      urlHash.includes('code=');

    const hasAccounts = this.authService.instance.getAllAccounts().length > 0;

    if (hasRedirectParams || hasAccounts) {
      this.isLoading = true;
    } else {
      this.isLoading = false;
    }

    // Redirect is handled in AppComponent; wait for MSAL then update UI.
    this.whenMsalIdle(() => {
      if (this.authService.instance.getAllAccounts().length === 0) {
        this.isLoading = false;
      }
      return of(null);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  login() {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;

    this.authService.instance
      .loginRedirect({
        scopes: ['openid', 'profile', 'offline_access'],
      })
      .catch((err) => {
        this.isLoading = false;

        if (err?.errorCode === 'interaction_in_progress') {
          console.warn('MSAL interaction_in_progress. Clearing session to unblock login.');
          sessionStorage.clear();
          return;
        }

        if (LoginComponent.msalCatchIndicatesB2cForgotPassword(err)) {
          this.isLoading = true;
          this.whenMsalIdle(() => {
            LoginComponent.removeB2cForgotPasswordErrorFromUrl();
            return from(
              this.authService.instance.loginRedirect({
                scopes: ['openid', 'profile'],
                authority: environment.passwordResetAuthority,
              }),
            );
          });
          return;
        }

        if (isBenignMsalRedirectError(err)) {
          console.warn('MSAL cache mismatch. Clearing session and retrying login silently to recover.');
          sessionStorage.clear();
          this.isLoading = false;
          this.login();
          return;
        }

        console.error('Login error:', err);
      });
  }

  /** Runs after MSAL redirect handling completes (handled once in AppComponent). */
  private whenMsalIdle(action: () => Observable<unknown>): void {
    this.msalRedirectService
      .handleRedirectOnce()
      .pipe(concatMap(action), takeUntil(this.destroy$))
      .subscribe({
        error: () => {
          this.isLoading = false;
        },
      });
  }
}
